import type { UseCaseContent } from "@/lib/use-cases/content";

export const PT_BR_USE_CASES: Partial<Record<string, UseCaseContent>> = {
  clippers: {
    related: [
      "/features/auto-clip",
      "/features/video-cutter",
      "/calculators/clips-from-one-video",
      "/calculators/views-to-money",
    ],
    navTitle: "Clipadores",
    seoTitle: "Ganhe dinheiro clipando vídeos: campanhas de clipagem",
    seoDescription:
      "Encontre campanhas de clipagem, corte clipes virais com IA e acompanhe suas views no Crayo. O jeito mais rápido de começar a clipar e ser pago.",
    heroPrefix: "Ganhe dinheiro",
    heroHighlight: "clipando vídeos",
    heroSuffix: "dos criadores que você já acompanha",
    description:
      "Explore campanhas de clipagem ativas na Content Library do Crayo, corte clipes em minutos com IA e acompanhe cada view. Encontre o trabalho e faça o trabalho em um só app.",
    buttonText: "Ver campanhas",
    buttonLink: "https://crayo.ai/content-library",
    steps: [
      {
        title: "Encontre uma campanha",
        description:
          "Abra a Content Library e explore campanhas ativas com recompensa por clipe e material de origem pronto para usar.",
      },
      {
        title: "Clipe em minutos",
        description:
          "O Auto Clip encontra os momentos mais fortes, ou use o Clip It para cortar exatamente o que você quer, com legendas aplicadas automaticamente.",
      },
      {
        title: "Poste e acompanhe",
        description:
          "Publique nas suas contas e acompanhe suas views com o View Tracker, para sempre saber o que está performando.",
      },
    ],
    benefits: [
      {
        title: "As campanhas vêm até você",
        description:
          "Sem DM fria nem caça ao tesouro no Discord. As campanhas ativas ficam dentro do Crayo com o material anexado.",
      },
      {
        title: "Momentos virais, encontrados para você",
        description:
          "O Auto Clip analisa vídeos longos e mostra os momentos que valem um post, para você não passar horas vasculhando VODs inteiros.",
      },
      {
        title: "Legendas em segundos",
        description:
          "Cada clipe recebe legendas limpas e estilizadas automaticamente, a diferença entre um clipe que é assistido e um que é ignorado no scroll.",
      },
      {
        title: "Conheça seus números",
        description:
          "O View Tracker acompanha seus posts em todas as contas, então medir o desempenho deixa de ser trabalho de planilha.",
      },
      {
        title: "Não precisa saber editar",
        description: "Se você sabe escolher um bom momento, o Crayo cuida do resto.",
      },
      {
        title: "Tudo em uma aba",
        description:
          "Campanhas, clipagem, legendas e acompanhamento vivem em um só app, sem pular entre ferramentas para achar trabalho e entregar.",
      },
    ],
    relatedFeatures: ["auto-clip", "video-cutter", "video-crop", "youtube-downloader"],
    faqs: [
      {
        question: "Como encontro campanhas de clipagem?",
        answer:
          "Elas ficam integradas na Content Library do Crayo. Abra, veja o que está ativo e comece a clipar. Os vídeos de origem são fornecidos pela campanha.",
      },
      {
        question: "Como funcionam os pagamentos?",
        answer:
          "Cada campanha define seus próprios termos e recompensas, mostrados antes de você começar a clipar. Leia os detalhes da campanha, clipe, poste e receba a recompensa conforme os termos dela.",
      },
      {
        question: "Preciso ter experiência com edição?",
        answer:
          "Não. O Auto Clip encontra os momentos, as legendas são automáticas e o editor cuida de cortes e enquadramentos. Bom gosto importa mais do que técnica.",
      },
      {
        question: "Posso clipar criadores que não têm campanhas ativas?",
        answer:
          "Sim. Cole qualquer link do YouTube ou envie o material e clipe do mesmo jeito. As campanhas são só o caminho mais rápido para ser pago.",
      },
    ],
  },
  "podcast-clipping": {
    related: [
      "/compare/crayo-vs-descript",
      "/compare/crayo-vs-riverside-magic-clips",
      "/features/auto-clip",
      "/features/speech-enhancer",
      "/use-cases/agencies",
      "/calculators/clips-from-one-video",
    ],
    navTitle: "Cortes de podcast",
    seoTitle: "Gerador de cortes de podcast: de episódios a shorts",
    seoDescription:
      "Transforme cada episódio em uma semana de cortes. A IA encontra os melhores momentos, limpa o áudio, legenda e recorta para TikTok, Reels e Shorts.",
    heroPrefix: "Transforme um episódio em uma",
    heroHighlight: "semana de cortes",
    heroSuffix: "",
    description:
      "Envie um episódio, ou só um link do YouTube, e deixe a IA encontrar os momentos que valem um post. Legendas, corte vertical e áudio limpo inclusos.",
    buttonText: "Clipe um episódio grátis",
    buttonLink: "https://crayo.ai/create/auto-clip",
    steps: [
      {
        title: "Adicione seu episódio",
        description:
          "Envie um arquivo ou cole um link do YouTube com o downloader integrado. Episódios completos são bem-vindos.",
      },
      {
        title: "Deixe a IA encontrar os momentos",
        description:
          "O Auto Clip analisa a conversa inteira e extrai os trechos com mais chance de prender quem está rolando o feed.",
      },
      {
        title: "Finalize e poste",
        description:
          "O melhorador de voz limpa o áudio, as legendas são aplicadas automaticamente e cada clipe é recortado em 9:16 para TikTok, Reels e Shorts.",
      },
    ],
    benefits: [
      {
        title: "Pare de vasculhar linhas do tempo",
        description:
          "Uma hora de conversa, analisada em minutos. Você revisa clipes, não material bruto.",
      },
      {
        title: "Áudio de podcast, resolvido",
        description:
          "O melhorador de voz limpa o eco da sala e os níveis irregulares para os cortes soarem como a versão de estúdio.",
      },
      {
        title: "Legendas que carregam o clipe",
        description:
          "A maioria assiste vídeo curto sem som. Cada clipe sai com legendas precisas e estilizadas.",
      },
      {
        title: "Vertical sem o corte esquisito",
        description:
          "Reenquadre episódios horizontais em 9:16 de forma limpa em vez de postar vídeo com barras pretas que ninguém assiste.",
      },
      {
        title: "Funciona a partir de um link",
        description:
          "Sem exportar da sua plataforma de podcast. Cole a URL do YouTube e comece a clipar.",
      },
      {
        title: "Não só podcasts",
        description:
          "Webinars, sessões de mentoria, aulas de curso, entrevistas: se é conteúdo longo falado, o Crayo clipa.",
      },
    ],
    relatedFeatures: ["auto-clip", "speech-enhancer", "youtube-downloader", "video-crop"],
    faqs: [
      {
        question: "Como o Crayo sabe quais momentos clipar?",
        answer:
          "O Auto Clip analisa o episódio inteiro e mostra os trechos com os ganchos mais fortes, como histórias, opiniões polêmicas e punchlines, e aí você escolhe quais postar.",
      },
      {
        question: "Sou coach ou crio cursos, não tenho podcast. Funciona com meu conteúdo?",
        answer:
          "Sim. Qualquer conteúdo longo e falado, de webinars e workshops a módulos de curso e calls com clientes, é clipado exatamente do mesmo jeito.",
      },
      {
        question: "Posso clipar direto do YouTube?",
        answer:
          "Sim. Cole o link, o Crayo baixa o vídeo com o downloader integrado e em minutos você está clipando. Clipe só conteúdo sobre o qual você tem os direitos.",
      },
      {
        question: "Como ficam os clipes finalizados?",
        answer:
          "Vídeo vertical 9:16 com legendas estilizadas embutidas e áudio limpo, pronto para postar no TikTok, Reels e Shorts do jeito que está.",
      },
    ],
  },
  streamers: {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-eklipse",
      "/compare/crayo-vs-streamladder",
    ],
    navTitle: "Streamers",
    seoTitle: "Criador de clipes da Twitch e Kick para streamers",
    seoDescription:
      "Transforme VODs de live em shorts que fazem seu canal crescer. A IA encontra os momentos de hype, legenda e formata clipes para TikTok, Reels e Shorts.",
    heroPrefix: "Transforme seus",
    heroHighlight: "VODs de live",
    heroSuffix: "em shorts que fazem o canal crescer",
    description:
      "O Crayo transforma VODs da Twitch e da Kick em clipes verticais curtos encontrando os momentos de mais energia e formatando para TikTok, Reels e Shorts. É para streamers que querem clipes feitos com o material que já gravaram.",
    buttonText: "Clipe seu VOD grátis",
    buttonLink: "https://crayo.ai/create/auto-clip",
    steps: [
      {
        title: "Traga um VOD",
        description:
          "Envie seu VOD da Twitch ou da Kick, ou pegue a exportação da sua live no YouTube com o downloader integrado.",
      },
      {
        title: "A IA encontra o hype",
        description:
          "O Auto Clip analisa horas de live e extrai as jogadas decisivas, os momentos de raiva e os trechos que explodiram o chat.",
      },
      {
        title: "Formate em vertical e poste",
        description:
          "Layout de streamer com sua facecam em destaque, legendas automáticas, corte 9:16, exportar.",
      },
    ],
    benefits: [
      {
        title: "Seus VODs são uma mina de conteúdo",
        description:
          "Cada live já contém seus melhores clipes curtos. O Crayo tira eles de lá para que parem de morrer no seu arquivo.",
      },
      {
        title: "Clipe depois de dormir, não em vez de dormir",
        description:
          "Fazer seis horas de live e depois editar por três é como streamer se esgota. O Auto Clip faz a vasculhada por você.",
      },
      {
        title: "Layouts com a facecam em destaque",
        description:
          "Formatação pensada para streamer: sua câmera e o gameplay, organizados para uma tela vertical.",
      },
      {
        title: "Organize o caos do áudio",
        description:
          "O melhorador de voz controla o áudio do jogo que vaza e as inconsistências do microfone para seus comentários realmente chegarem.",
      },
      {
        title: "Shorts são seu motor de descoberta",
        description:
          "TikTok e Shorts alcançam gente que nunca vai rolar um diretório da Twitch. Postar clipes com constância é como streamers pequenos são descobertos.",
      },
      {
        title: "Poste enquanto o momento está quente",
        description:
          "Clipe a live de hoje em minutos, para o short subir enquanto o chat ainda está falando disso.",
      },
    ],
    relatedFeatures: ["auto-clip", "speech-enhancer", "video-crop", "video-cutter"],
    faqs: [
      {
        question: "Funciona tanto com Twitch quanto com Kick?",
        answer:
          "Sim. Traga seu VOD de qualquer uma das duas plataformas (ou seu arquivo de lives no YouTube) e clipe do mesmo jeito.",
      },
      {
        question: "Minhas lives passam de 4 horas. Dá conta?",
        answer:
          "É justamente para isso. O Auto Clip foi feito para material longo. Ele analisa o VOD inteiro e te entrega os momentos, então a duração é uma vantagem, não um problema.",
      },
      {
        question: "Clipes realmente fazem uma live crescer?",
        answer:
          "Vídeo curto é onde novos espectadores descobrem streamers que ainda não são grandes. Clipes não substituem uma boa live, mas são a fonte de descoberta mais confiável que um streamer pequeno tem.",
      },
      {
        question: "Preciso da minha facecam em todo clipe?",
        answer:
          "Não. O layout de streamer é opcional, então você pode usar gameplay em tela cheia, facecam em destaque ou layout dividido em cada clipe.",
      },
    ],
  },
};
